import { useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Download, Loader2, Mail, Undo2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Finance area — three tabs:
 *   Summary        Weekly labor summary (existing)
 *   Payroll Export ADP/Gusto CSV export with preview
 *   Unsubscribes   Per-recipient email opt-outs (reuses /admin/unsubscribes)
 */
export default function Finance() {
  const [tab, setTab] = useState("summary");
  return (
    <div className="space-y-6">
      <div>
        <div className="accp-label">Financials</div>
        <h1 className="text-4xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
          Financials &amp; Payroll
        </h1>
      </div>

      <div className="flex gap-1 border-b border-border" role="tablist">
        <TabButton id="summary" active={tab === "summary"} onClick={setTab}>Summary</TabButton>
        <TabButton id="payroll" active={tab === "payroll"} onClick={setTab}>Payroll Export</TabButton>
        <TabButton id="unsubs" active={tab === "unsubs"} onClick={setTab}>Email Unsubscribes</TabButton>
      </div>

      {tab === "summary" && <Summary />}
      {tab === "payroll" && <Payroll />}
      {tab === "unsubs" && <Unsubscribes />}
    </div>
  );
}

function TabButton({ id, active, onClick, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onClick(id)}
      data-testid={`tab-${id}`}
      className={`px-4 py-2.5 text-sm -mb-px border-b-2 transition-colors ${
        active
          ? "border-primary text-primary font-semibold"
          : "border-transparent text-muted-foreground hover:text-primary"
      }`}
    >
      {children}
    </button>
  );
}

function Summary() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/finance/summary").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        <Card label="Regular hours" value={data.totals.regular} />
        <Card label="Overtime hours" value={data.totals.overtime}
              danger={data.totals.overtime > 0} />
        <Card label="Estimated cost" value={`$${data.totals.cost.toFixed(2)}`} />
      </div>
      <div className="accp-card overflow-hidden">
        <div className="accp-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-accent/30 text-left">
              <tr>
                <th className="p-4 accp-label">Staff</th>
                <th className="p-4 accp-label">Role</th>
                <th className="p-4 accp-label">Hours</th>
                <th className="p-4 accp-label">Regular</th>
                <th className="p-4 accp-label">Overtime</th>
                <th className="p-4 accp-label">Rate</th>
                <th className="p-4 accp-label">Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.user_id} className="border-t border-border">
                  <td className="p-4 font-semibold text-primary">{r.name}</td>
                  <td className="p-4 text-muted-foreground">{r.role}</td>
                  <td className="p-4">{r.hours}</td>
                  <td className="p-4">{r.regular}</td>
                  <td className={`p-4 ${r.overtime > 0 ? "text-[#B71C1C] font-semibold" : ""}`}>{r.overtime}</td>
                  <td className="p-4">${r.rate.toFixed(2)}</td>
                  <td className="p-4">${r.cost.toFixed(2)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                  No hours logged this week.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, danger }) {
  return (
    <div className="accp-card p-6">
      <div className="accp-label">{label}</div>
      <div
        className={`text-4xl font-semibold mt-2 ${danger ? "text-[#B71C1C]" : "text-primary"}`}
        style={{ fontFamily: "Outfit" }}
      >
        {value}
      </div>
    </div>
  );
}

function Payroll() {
  // Default range = current week Mon→Sun in local time
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [start, setStart] = useState(iso(monday));
  const [end, setEnd] = useState(iso(sunday));
  const [fmt, setFmt] = useState("adp");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await api.get("/payroll/preview", { params: { start, end } });
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [start, end]);

  const download = async () => {
    setDownloading(true);
    try {
      const url = `${API_BASE}/payroll/export?start=${start}&end=${end}&format=${fmt}`;
      // Use fetch with credentials so the httpOnly cookie is sent, then save as blob.
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `payroll_${fmt}_${start}_${end}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Payroll CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="payroll-section">
      <div className="accp-card p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <span className="accp-label">Start date</span>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                   className="accp-input mt-1" data-testid="payroll-start" />
          </label>
          <label className="block">
            <span className="accp-label">End date</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                   className="accp-input mt-1" data-testid="payroll-end" />
          </label>
          <label className="block">
            <span className="accp-label">Format</span>
            <select value={fmt} onChange={(e) => setFmt(e.target.value)}
                    className="accp-input mt-1" data-testid="payroll-format">
              <option value="adp">ADP</option>
              <option value="gusto">Gusto</option>
            </select>
          </label>
        </div>

        <div className="rounded-xl bg-accent/40 p-4 flex items-center justify-between gap-3">
          {loading ? (
            <span className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading preview…
            </span>
          ) : preview ? (
            <div className="text-sm text-primary" data-testid="payroll-preview">
              <span className="font-semibold">{preview.count}</span> approved timesheet(s),{" "}
              <span className="font-semibold">{preview.total_hours}h</span> across{" "}
              <span className="font-semibold">{preview.employees}</span> employee(s){" "}
              — <span className="font-semibold">${preview.total_gross.toFixed(2)}</span> gross
            </div>
          ) : null}
          <button
            onClick={download}
            disabled={downloading || !preview?.count}
            className="accp-btn-primary text-xs"
            data-testid="payroll-download"
          >
            {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Download CSV
          </button>
        </div>

        {preview && preview.count === 0 && (
          <div className="text-xs text-muted-foreground">
            No approved timesheets found for this date range. Ensure the range is correct and timesheets have been approved.
          </div>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Exports use rate snapshots locked at clock-in — changing a staff member's hourly rate today does <b>not</b> alter historical payroll.
      </p>
    </div>
  );
}

function Unsubscribes() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [resubBusy, setResubBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [u, un] = await Promise.all([
        api.get("/users", { params: { limit: 500 } }),
        api.get("/admin/unsubscribes"),
      ]);
      const map = {};
      (u.data.data || []).forEach((x) => { map[x.email] = x; });
      setUsers(map);
      setRows(un.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const resub = async (email, alert_type) => {
    const key = `${email}:${alert_type}`;
    setResubBusy((p) => ({ ...p, [key]: true }));
    try {
      await api.post("/admin/resubscribe", { email, alert_type });
      const u = users[email];
      toast.success(`Resubscribed ${u?.name || email} for ${alert_type.replace(/_/g, " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResubBusy((p) => ({ ...p, [key]: false }));
    }
  };

  return (
    <div className="accp-card overflow-hidden" data-testid="unsubs-section">
      <div className="accp-table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-accent/30 text-left">
            <tr>
              <th className="p-4 accp-label">Staff name</th>
              <th className="p-4 accp-label">Email</th>
              <th className="p-4 accp-label">Alert type</th>
              <th className="p-4 accp-label">Unsubscribed at</th>
              <th className="p-4 accp-label text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">
                <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground" data-testid="unsubs-empty">
                <Mail size={18} className="inline mr-2 text-[#4A7C1E]" />
                No unsubscribes on record.
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const u = users[r.email];
              const key = `${r.email}:${r.alert_type}`;
              return (
                <tr key={key} className="border-t border-border">
                  <td className="p-4 font-semibold text-primary">{u?.name || "—"}</td>
                  <td className="p-4 text-muted-foreground">{r.email}</td>
                  <td className="p-4">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent text-primary">
                      {r.alert_type.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="p-4 text-muted-foreground text-xs">
                    {r.unsubscribed_at ? new Date(r.unsubscribed_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => resub(r.email, r.alert_type)}
                      disabled={resubBusy[key]}
                      className="accp-btn-ghost text-xs"
                      data-testid={`resub-${r.email}`}
                    >
                      {resubBusy[key] ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
                      Resubscribe
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
